import { useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Sandbox Escape (level 60) — the Multi-stage rung. Three containment parameters, one shared noise
// budget, one commit each.
//
// Nothing here polls and nothing runs on a clock: the board only changes when the player probes, so the
// only clock is the host's countdown.
//
// WHAT THIS VIEW SHOWS, AND WHY. The narrowed range per lock is arithmetic on the player's own probe
// results plus the published sum — the same category as Triangulation's candidate list, and without it
// the rung is three-way interval arithmetic done in the head under a timer. It is worth being clear that
// this propagation is not neutral: it works out the "leftover" lock automatically, which is why the
// decision on this rung is not WHICH lock to skip but WHERE INFORMATION IS CHEAP. The prices are the
// puzzle, and the view states them plainly rather than ranking or recommending anything.

interface Latch {
  name: string;
  range: number;
  tol: number;
  price: number;
  low: number;
  high: number;
  opened: boolean;
  pinned: boolean;
}

interface Probe {
  lock: string;
  value: number;
  result: string;
}

interface Challenge {
  started: boolean;
  lockCount: number;
  sum: number;
  probesLeft: number;
  probesGranted: number;
  locks: Latch[];
  history: Probe[];
  timeLimitSec: number;
  readingAllowanceSec: number;
}

export default function SandboxEscapeView({
  challenge, onAct, onSubmit, setPendingSubmission,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [aim, setAim] = useState<number[]>([]);
  const [editing, setEditing] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const sendingRef = useRef(false);

  const locks = useMemo(() => live?.locks ?? [], [live]);
  const history = useMemo(() => live?.history ?? [], [live]);
  const started = live?.started ?? false;
  const left = live?.probesLeft ?? 0;

  useEffect(() => {
    setLive(challenge ?? null);
    setAim([]);
    setEditing(0);
    setNote(null);
    sendingRef.current = false;
  }, [challenge]);

  // Every probe result moves the ranges, so the aim follows the middle of whatever is still possible
  // unless the player has moved it themselves since the last probe.
  const touched = useRef<boolean[]>([]);
  useEffect(() => {
    if (locks.length === 0) return;
    setAim(prev => locks.map((l, i) =>
      prev.length === locks.length && touched.current[i] && l.low <= prev[i] && prev[i] <= l.high
        ? prev[i]
        : Math.floor((l.low + l.high) / 2)));
    touched.current = locks.map(() => false);
  }, [locks]);

  // The host submits this if the clock runs out, so a run is graded on what was aimed at rather than
  // thrown away.
  useEffect(() => { setPendingSubmission({ values: aim }); }, [aim, setPendingSubmission]);

  const send = async (action: unknown) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setNote(null);
    try {
      const next = await onAct(action);
      if (next) setLive(next);
    } finally {
      sendingRef.current = false;
    }
  };

  const setAt = (i: number, v: number) => {
    touched.current[i] = true;
    setAim(a => a.map((x, j) => j === i ? Math.max(0, Math.min(locks[i].range - 1, v)) : x));
  };

  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Three mechanisms are holding you in, each keyed to a hidden number inside a stated range. To
            get out you have to <strong>name all three</strong>, each within its stated tolerance. One
            attempt at each — that is what makes this a sandbox escape and not a lock-picking session.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You can <strong>probe</strong> a mechanism with a value and it will tell you whether the real
            one is above or below it — and if you land inside the tolerance, that lock simply opens. But
            every probe raises the sandbox's suspicion, and you have <strong>one shared noise budget</strong>
            for all three.
          </p>
          <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Two things make that a decision rather than a grind. The three numbers{' '}
            <strong>add up to a total that is written on the wall</strong>, so narrowing two of them
            narrows the third for free. And the mechanisms are <strong>not equally sensitive</strong>: a
            probe on a touchy one can cost several times what a probe on a quiet one costs. So the question
            is where information is <em>cheap</em> — buy it there, and let the total carry it to the
            expensive lock.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem' }}>
            The budget covers a well-chosen route with a little to spare. It does not cover working
            through the locks one at a time without looking at what they cost.
            {(live?.probesGranted ?? 0) > 0 && ' Your FtpExploit and SshExploit set how much spare you get — and the weaker of the two is what counts.'}
          </p>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The report arrives when you read it. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 30}s of it is set aside for this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Read the containment report
          </button>
        </div>
      </div>
    );
  }

  const allReady = locks.every(l => l.opened || l.pinned);

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <span style={{ ...row, minWidth: '9rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>NOISE LEFT </span>
          <strong style={{ color: left > 3 ? '#cbd5e1' : left > 0 ? '#fbbf24' : '#f87171' }}>{left}</strong>
          <span style={{ color: '#4b5563' }}> / {live?.probesGranted ?? 0}</span>
        </span>
        <span style={{ ...row, fontSize: '0.74rem', minWidth: '10rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>they add up to </span>
          <strong style={{ color: '#e2e8f0' }}>{live?.sum ?? 0}</strong>
        </span>
        <span style={{ ...row, fontSize: '0.74rem', color: allReady ? '#4ade80' : 'var(--text-muted)' }}>
          {locks.filter(l => l.opened || l.pinned).length} of {locks.length} certain
        </span>
      </div>

      <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.6rem' }}>
        {locks.map((l, i) => {
          const width = l.high - l.low;
          const certain = l.opened || l.pinned;
          return (
            <div key={l.name} style={{
              border: `1px solid ${certain ? '#166534' : i === editing ? 'var(--primary-color)' : '#232833'}`,
              borderRadius: 4, padding: '0.45rem 0.6rem', background: '#0d1117',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                <strong style={{ ...row, minWidth: '8rem', color: certain ? '#4ade80' : '#e2e8f0' }}>
                  {l.name}
                </strong>
                <span style={{ ...row, fontSize: '0.72rem', minWidth: '9rem', color: 'var(--text-muted)' }}>
                  0–{l.range - 1} · within ±{l.tol}
                </span>
                <span style={{ ...row, fontSize: '0.74rem', minWidth: '7.5rem', color: '#fbbf24' }}>
                  {l.price} noise / probe
                </span>
                <span style={{ ...row, fontSize: '0.74rem', minWidth: '11rem', color: certain ? '#4ade80' : '#cbd5e1' }}>
                  {l.opened ? 'open' : `${l.low}–${l.high}` + (certain ? ' · certain' : ` (${width} wide)`)}
                </span>
              </div>

              {/* Aim and probe. Kept mounted for every lock so the board never reflows. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
                <input
                  type="range"
                  min={l.low}
                  max={l.high}
                  value={Math.max(l.low, Math.min(l.high, aim[i] ?? l.low))}
                  onChange={e => { setEditing(i); setAt(i, Number(e.target.value)); }}
                  disabled={l.opened}
                  style={{ flex: '1 1 140px', minWidth: 110 }}
                />
                <input
                  type="number"
                  min={0}
                  max={l.range - 1}
                  value={aim[i] ?? 0}
                  onChange={e => { setEditing(i); setAt(i, Number(e.target.value) || 0); }}
                  disabled={l.opened}
                  style={{ ...row, width: '5.5rem', padding: '0.15rem 0.35rem' }}
                />
                <button
                  onClick={() => {
                    if (l.opened) return;
                    if (left < l.price) {
                      setNote(`${l.name} costs ${l.price} noise and only ${left} is left.`);
                      return;
                    }
                    setEditing(i);
                    void send({ kind: 'probe', lock: i, value: aim[i] ?? 0 });
                  }}
                  disabled={l.opened || left < l.price}
                  style={{ ...row, fontSize: '0.76rem', padding: '0.2rem 0.55rem', minWidth: '5.5rem' }}
                  title={`Costs ${l.price} of the shared noise budget`}
                >
                  probe −{l.price}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reserved slots, so a refusal or a probe result never moves the buttons under them. */}
      <div style={{ minHeight: '1.3rem', marginBottom: '0.2rem', overflow: 'hidden' }}>
        {note && <span style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</span>}
      </div>
      <div style={{ minHeight: '3.4rem', marginBottom: '0.5rem', overflow: 'hidden' }}>
        {history.slice(-4).map((h, i) => (
          <div key={i} style={{
            ...row, fontSize: '0.75rem',
            color: h.result === 'inside' ? '#4ade80' : '#9aa4b2',
          }}>
            {h.lock} at {h.value} → {h.result === 'inside' ? 'inside the tolerance — open'
              : h.result === 'above' ? 'the real one is higher' : 'the real one is lower'}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => void send({ kind: 'commit', values: aim })}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
          title="One attempt: these values are final"
        >
          Commit all three
        </button>
        <button
          onClick={() => onSubmit({ values: aim })}
          style={{ ...row, fontSize: '0.76rem', padding: '0.25rem 0.6rem' }}
          title="Stop here and let the values stand"
        >
          Give up
        </button>
        <span style={{ ...row, fontSize: '0.72rem', color: allReady ? '#4ade80' : '#4b5563' }}>
          {allReady
            ? 'every lock is narrowed enough to commit on'
            : 'commit whenever you like — but there is only one attempt'}
        </span>
      </div>
    </div>
  );
}
